import { v4 as uuidv4 } from 'uuid';
import { arrayMove } from '@dnd-kit/sortable';

/**
 * Custom hook que contiene todos los handlers para manipular inputs del formulario
 * @param {Object} form - Estado del formulario
 * @param {Function} setForm - Setter del estado del formulario
 * @returns {Object} Objeto con todos los handlers
 */
const useFormHandlers = (form, setForm) => {
  
  // Handler para cambiar el título de un input
  const handleTitleChange = (id, newTitle) => {
    const updatedInputs = { ...form.inputs };
    updatedInputs[id].title = newTitle;
    updatedInputs[id].name = newTitle;
    setForm({ ...form, inputs: updatedInputs });
  };

  // Handler para actualizar propiedades de un input
  const handleInputUpdate = (title, updatedInput) => {
    setForm((prevForm) => ({
      ...prevForm,
      inputs: {
        ...prevForm.inputs,
        [title]: {
          ...prevForm.inputs[title],
          ...updatedInput,
        },
      },
    }));
  };

  // Handler para cambiar el campo requerido
  const handleRequiredChange = (id, isRequired) => {
    const updatedInputs = { ...form.inputs };
    updatedInputs[id].required = isRequired;
    setForm({ ...form, inputs: updatedInputs });
  };

  // Handler para cambiar el tamaño del input
  const handleSizeChange = (id, isChecked, size) => {
    const updatedInputs = { ...form.inputs };
    if (isChecked) {
      updatedInputs[id].size = size;
    } else {
      delete updatedInputs[id].size;
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  // Handlers para opciones de DROPBOX/MULTI_DROPBOX
  const handleAddOption = (title) => {
    const updatedInputs = { ...form.inputs };
    updatedInputs[title].dropbox = updatedInputs[title].dropbox ? [...updatedInputs[title].dropbox, ''] : [''];
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleRemoveOption = (title, optionIndex) => {
    const updatedInputs = { ...form.inputs };
    updatedInputs[title].dropbox.splice(optionIndex, 1);
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleOptionChange = (title, optionIndex, value) => {
    const updatedInputs = { ...form.inputs };
    updatedInputs[title].dropbox[optionIndex] = value;
    setForm({ ...form, inputs: updatedInputs });
  };

  // Handler para eliminar un input
  const handleRemoveInput = (title) => {
    const updatedInputs = { ...form.inputs };
    delete updatedInputs[title];
    setForm({ ...form, inputs: updatedInputs });
  };

  // Handler para añadir un nuevo input
  const handleAddInput = (type) => {
    const newInputId = uuidv4();
    const newOrder = Object.keys(form.inputs).length;
    setForm({
      ...form,
      inputs: {
        ...form.inputs,
        [newInputId]: {
          title: `Input ${Object.keys(form.inputs).length + 1}`,
          id: newInputId,
          name: '',
          order: newOrder,
          type: type,
          required: true
        }
      }
    });
    return newInputId;
  };

  // Handler para reordenar inputs (drag and drop)
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const inputIds = Object.keys(form.inputs);
      const oldIndex = inputIds.indexOf(active.id);
      const newIndex = inputIds.indexOf(over.id);
      const newInputIds = arrayMove(inputIds, oldIndex, newIndex);
      
      const reorderedInputs = {};
      newInputIds.forEach((id, index) => {
        reorderedInputs[id] = {
          ...form.inputs[id],
          order: index
        };
      });
      
      setForm(prev => ({ ...prev, inputs: reorderedInputs }));
    }
  };

  // Handlers para campos condicionales (actualizaciones inmutables para que React detecte cambios)
  const handleAddConditionalField = (inputId, optionIndex) => {
    setForm((prev) => {
      const input = prev.inputs[inputId];
      if (!input) return prev;
      const conditionalFields = { ...(input.conditionalFields || {}) };
      const optionFields = [...(conditionalFields[optionIndex] || [])];
      optionFields.push({
        title: '',
        name: '',
        type: 'TEXT',
        required: true,
        triggerOption: input.dropbox?.[optionIndex] || ''
      });
      conditionalFields[optionIndex] = optionFields;
      return {
        ...prev,
        inputs: {
          ...prev.inputs,
          [inputId]: { ...input, conditionalFields }
        }
      };
    });
  };

  const handleRemoveConditionalField = (inputId, optionIndex, fieldIndex) => {
    setForm((prev) => {
      const input = prev.inputs[inputId];
      if (!input?.conditionalFields?.[optionIndex]) return prev;
      const optionFields = input.conditionalFields[optionIndex].filter((_, i) => i !== fieldIndex);
      const conditionalFields = { ...input.conditionalFields };
      if (optionFields.length === 0) {
        delete conditionalFields[optionIndex];
      } else {
        conditionalFields[optionIndex] = optionFields;
      }
      const newConditionalFields = Object.keys(conditionalFields).length === 0 ? undefined : conditionalFields;
      return {
        ...prev,
        inputs: {
          ...prev.inputs,
          [inputId]: { ...input, conditionalFields: newConditionalFields }
        }
      };
    });
  };

  const handleConditionalFieldChange = (inputId, optionIndex, fieldIndex, field, value) => {
    setForm((prev) => {
      const input = prev.inputs[inputId];
      const condField = input?.conditionalFields?.[optionIndex]?.[fieldIndex];
      if (!condField) return prev;
      const optionFields = [...(input.conditionalFields[optionIndex] || [])];
      const updatedCondField = { ...condField, [field]: value };
      if (field === 'title') updatedCondField.name = value;
      updatedCondField.triggerOption = input.dropbox?.[optionIndex] || '';
      if (field === 'type' && value === 'DROPBOX' && !condField.dropbox) {
        updatedCondField.dropbox = [''];
        updatedCondField.conditionalFields = updatedCondField.conditionalFields || {};
      }
      if (field === 'type' && value === 'REPEATABLE_SECTION') {
        updatedCondField.sectionFields = condField.sectionFields || [];
        updatedCondField.minInstances = condField.minInstances ?? 1;
        updatedCondField.maxInstances = condField.maxInstances ?? 10;
        delete updatedCondField.dropbox;
      }
      optionFields[fieldIndex] = updatedCondField;
      const conditionalFields = { ...input.conditionalFields, [optionIndex]: optionFields };
      return {
        ...prev,
        inputs: {
          ...prev.inputs,
          [inputId]: { ...input, conditionalFields }
        }
      };
    });
  };

  // Handlers para opciones de dropbox condicional
  const handleAddConditionalDropboxOption = (inputId, optionIndex, fieldIndex) => {
    setForm((prev) => {
      const input = prev.inputs[inputId];
      const condField = input?.conditionalFields?.[optionIndex]?.[fieldIndex];
      if (!condField) return prev;
      const dropbox = [...(condField.dropbox || []), ''];
      const optionFields = [...(input.conditionalFields[optionIndex] || [])];
      optionFields[fieldIndex] = { ...condField, dropbox };
      const conditionalFields = { ...input.conditionalFields, [optionIndex]: optionFields };
      return {
        ...prev,
        inputs: {
          ...prev.inputs,
          [inputId]: { ...input, conditionalFields }
        }
      };
    });
  };

  const handleRemoveConditionalDropboxOption = (inputId, optionIndex, fieldIndex, dropOptIndex) => {
    setForm((prev) => {
      const input = prev.inputs[inputId];
      const condField = input?.conditionalFields?.[optionIndex]?.[fieldIndex];
      if (!condField?.dropbox) return prev;
      const dropbox = condField.dropbox.filter((_, i) => i !== dropOptIndex);
      const optionFields = [...(input.conditionalFields[optionIndex] || [])];
      optionFields[fieldIndex] = { ...condField, dropbox };
      const conditionalFields = { ...input.conditionalFields, [optionIndex]: optionFields };
      return {
        ...prev,
        inputs: {
          ...prev.inputs,
          [inputId]: { ...input, conditionalFields }
        }
      };
    });
  };

  const handleConditionalDropboxOptionChange = (inputId, optionIndex, fieldIndex, dropOptIndex, value) => {
    setForm((prev) => {
      const input = prev.inputs[inputId];
      const condField = input?.conditionalFields?.[optionIndex]?.[fieldIndex];
      if (!condField?.dropbox || dropOptIndex >= condField.dropbox.length) return prev;
      const dropbox = [...condField.dropbox];
      dropbox[dropOptIndex] = value;
      const optionFields = [...(input.conditionalFields[optionIndex] || [])];
      optionFields[fieldIndex] = { ...condField, dropbox };
      const conditionalFields = { ...input.conditionalFields, [optionIndex]: optionFields };
      return {
        ...prev,
        inputs: {
          ...prev.inputs,
          [inputId]: { ...input, conditionalFields }
        }
      };
    });
  };

  // Handlers para campos condicionales anidados (dentro de un dropbox condicional)
  const handleAddNestedConditionalField = (inputId, optionIndex, fieldIndex, nestedOptionIndex) => {
    setForm((prev) => {
      const input = prev.inputs[inputId];
      const condField = input?.conditionalFields?.[optionIndex]?.[fieldIndex];
      if (!condField) return prev;
      const nestedConditionalFields = { ...(condField.conditionalFields || {}) };
      const nestedOptionFields = [...(nestedConditionalFields[nestedOptionIndex] || [])];
      nestedOptionFields.push({
        title: '',
        name: '',
        type: 'TEXT',
        required: true,
        triggerOption: condField.dropbox?.[nestedOptionIndex] || ''
      });
      nestedConditionalFields[nestedOptionIndex] = nestedOptionFields;
      const optionFields = [...(input.conditionalFields[optionIndex] || [])];
      optionFields[fieldIndex] = { ...condField, conditionalFields: nestedConditionalFields };
      const conditionalFields = { ...input.conditionalFields, [optionIndex]: optionFields };
      return {
        ...prev,
        inputs: {
          ...prev.inputs,
          [inputId]: { ...input, conditionalFields }
        }
      };
    });
  };

  const handleRemoveNestedConditionalField = (inputId, optionIndex, fieldIndex, nestedOptionIndex, nestedFieldIndex) => {
    setForm((prev) => {
      const input = prev.inputs[inputId];
      const condField = input?.conditionalFields?.[optionIndex]?.[fieldIndex];
      if (!condField?.conditionalFields?.[nestedOptionIndex]) return prev;
      const nestedOptionFields = condField.conditionalFields[nestedOptionIndex].filter((_, i) => i !== nestedFieldIndex);
      const nestedConditionalFields = { ...condField.conditionalFields };
      if (nestedOptionFields.length === 0) {
        delete nestedConditionalFields[nestedOptionIndex];
      } else {
        nestedConditionalFields[nestedOptionIndex] = nestedOptionFields;
      }
      const newNested = Object.keys(nestedConditionalFields).length === 0 ? undefined : nestedConditionalFields;
      const optionFields = [...(input.conditionalFields[optionIndex] || [])];
      optionFields[fieldIndex] = { ...condField, conditionalFields: newNested };
      const conditionalFields = { ...input.conditionalFields, [optionIndex]: optionFields };
      return {
        ...prev,
        inputs: {
          ...prev.inputs,
          [inputId]: { ...input, conditionalFields }
        }
      };
    });
  };

  const handleNestedConditionalFieldChange = (inputId, optionIndex, fieldIndex, nestedOptionIndex, nestedFieldIndex, field, value) => {
    setForm((prev) => {
      const input = prev.inputs[inputId];
      const condField = input?.conditionalFields?.[optionIndex]?.[fieldIndex];
      const nestedField = condField?.conditionalFields?.[nestedOptionIndex]?.[nestedFieldIndex];
      if (!nestedField) return prev;
      const nestedOptionFields = [...(condField.conditionalFields[nestedOptionIndex] || [])];
      const updatedNested = { ...nestedField, [field]: value };
      if (field === 'title') updatedNested.name = value;
      updatedNested.triggerOption = condField.dropbox?.[nestedOptionIndex] || '';
      if (field === 'type' && value === 'DROPBOX' && !nestedField.dropbox) {
        updatedNested.dropbox = [''];
        updatedNested.conditionalFields = updatedNested.conditionalFields || {};
      }
      if (field === 'type' && value === 'REPEATABLE_SECTION') {
        updatedNested.sectionFields = nestedField.sectionFields || [];
        updatedNested.minInstances = nestedField.minInstances ?? 1;
        updatedNested.maxInstances = nestedField.maxInstances ?? 10;
        delete updatedNested.dropbox;
        delete updatedNested.conditionalFields;
      }
      nestedOptionFields[nestedFieldIndex] = updatedNested;
      const nestedConditionalFields = { ...condField.conditionalFields, [nestedOptionIndex]: nestedOptionFields };
      const optionFields = [...(input.conditionalFields[optionIndex] || [])];
      optionFields[fieldIndex] = { ...condField, conditionalFields: nestedConditionalFields };
      const conditionalFields = { ...input.conditionalFields, [optionIndex]: optionFields };
      return {
        ...prev,
        inputs: {
          ...prev.inputs,
          [inputId]: { ...input, conditionalFields }
        }
      };
    });
  };

  const handleAddNestedConditionalDropboxOption = (inputId, optionIndex, fieldIndex, nestedOptionIndex, nestedFieldIndex) => {
    setForm((prev) => {
      const input = prev.inputs[inputId];
      const condField = input?.conditionalFields?.[optionIndex]?.[fieldIndex];
      const nestedField = condField?.conditionalFields?.[nestedOptionIndex]?.[nestedFieldIndex];
      if (!nestedField) return prev;
      const dropbox = [...(nestedField.dropbox || []), ''];
      const nestedOptionFields = [...(condField.conditionalFields[nestedOptionIndex] || [])];
      nestedOptionFields[nestedFieldIndex] = { ...nestedField, dropbox };
      const nestedConditionalFields = { ...condField.conditionalFields, [nestedOptionIndex]: nestedOptionFields };
      const optionFields = [...(input.conditionalFields[optionIndex] || [])];
      optionFields[fieldIndex] = { ...condField, conditionalFields: nestedConditionalFields };
      const conditionalFields = { ...input.conditionalFields, [optionIndex]: optionFields };
      return {
        ...prev,
        inputs: {
          ...prev.inputs,
          [inputId]: { ...input, conditionalFields }
        }
      };
    });
  };

  const handleRemoveNestedConditionalDropboxOption = (inputId, optionIndex, fieldIndex, nestedOptionIndex, nestedFieldIndex, dropOptIndex) => {
    setForm((prev) => {
      const input = prev.inputs[inputId];
      const condField = input?.conditionalFields?.[optionIndex]?.[fieldIndex];
      const nestedField = condField?.conditionalFields?.[nestedOptionIndex]?.[nestedFieldIndex];
      if (!nestedField?.dropbox) return prev;
      const dropbox = nestedField.dropbox.filter((_, i) => i !== dropOptIndex);
      const nestedOptionFields = [...(condField.conditionalFields[nestedOptionIndex] || [])];
      nestedOptionFields[nestedFieldIndex] = { ...nestedField, dropbox };
      const nestedConditionalFields = { ...condField.conditionalFields, [nestedOptionIndex]: nestedOptionFields };
      const optionFields = [...(input.conditionalFields[optionIndex] || [])];
      optionFields[fieldIndex] = { ...condField, conditionalFields: nestedConditionalFields };
      const conditionalFields = { ...input.conditionalFields, [optionIndex]: optionFields };
      return {
        ...prev,
        inputs: {
          ...prev.inputs,
          [inputId]: { ...input, conditionalFields }
        }
      };
    });
  };

  const handleNestedConditionalDropboxOptionChange = (inputId, optionIndex, fieldIndex, nestedOptionIndex, nestedFieldIndex, dropOptIndex, value) => {
    setForm((prev) => {
      const input = prev.inputs[inputId];
      const condField = input?.conditionalFields?.[optionIndex]?.[fieldIndex];
      const nestedField = condField?.conditionalFields?.[nestedOptionIndex]?.[nestedFieldIndex];
      if (!nestedField?.dropbox || dropOptIndex >= nestedField.dropbox.length) return prev;
      const dropbox = [...nestedField.dropbox];
      dropbox[dropOptIndex] = value;
      const nestedOptionFields = [...(condField.conditionalFields[nestedOptionIndex] || [])];
      nestedOptionFields[nestedFieldIndex] = { ...nestedField, dropbox };
      const nestedConditionalFields = { ...condField.conditionalFields, [nestedOptionIndex]: nestedOptionFields };
      const optionFields = [...(input.conditionalFields[optionIndex] || [])];
      optionFields[fieldIndex] = { ...condField, conditionalFields: nestedConditionalFields };
      const conditionalFields = { ...input.conditionalFields, [optionIndex]: optionFields };
      return {
        ...prev,
        inputs: {
          ...prev.inputs,
          [inputId]: { ...input, conditionalFields }
        }
      };
    });
  };

  // Handlers para secciones repetibles
  const handleAddSectionField = (inputId) => {
    const updatedInputs = { ...form.inputs };
    if (!updatedInputs[inputId].sectionFields) {
      updatedInputs[inputId].sectionFields = [];
    }
    updatedInputs[inputId].sectionFields.push({
      title: '',
      name: '',
      type: 'TEXT',
      required: true
    });
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleRemoveSectionField = (inputId, fieldIndex) => {
    const updatedInputs = { ...form.inputs };
    if (updatedInputs[inputId].sectionFields) {
      updatedInputs[inputId].sectionFields.splice(fieldIndex, 1);
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleSectionFieldChange = (inputId, fieldIndex, field, value) => {
    const updatedInputs = { ...form.inputs };
    if (updatedInputs[inputId].sectionFields?.[fieldIndex]) {
      updatedInputs[inputId].sectionFields[fieldIndex][field] = value;
      if (field === 'title') {
        updatedInputs[inputId].sectionFields[fieldIndex].name = value;
      }
      if (field === 'type' && value === 'DROPBOX' && !updatedInputs[inputId].sectionFields[fieldIndex].dropbox) {
        updatedInputs[inputId].sectionFields[fieldIndex].dropbox = [''];
      }
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleAddSectionFieldDropboxOption = (inputId, fieldIndex) => {
    const updatedInputs = { ...form.inputs };
    if (updatedInputs[inputId].sectionFields?.[fieldIndex]) {
      if (!updatedInputs[inputId].sectionFields[fieldIndex].dropbox) {
        updatedInputs[inputId].sectionFields[fieldIndex].dropbox = [];
      }
      updatedInputs[inputId].sectionFields[fieldIndex].dropbox.push('');
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleRemoveSectionFieldDropboxOption = (inputId, fieldIndex, dropOptIndex) => {
    const updatedInputs = { ...form.inputs };
    if (updatedInputs[inputId].sectionFields?.[fieldIndex]?.dropbox) {
      updatedInputs[inputId].sectionFields[fieldIndex].dropbox.splice(dropOptIndex, 1);
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleSectionFieldDropboxOptionChange = (inputId, fieldIndex, dropOptIndex, value) => {
    const updatedInputs = { ...form.inputs };
    if (updatedInputs[inputId].sectionFields?.[fieldIndex]?.dropbox) {
      updatedInputs[inputId].sectionFields[fieldIndex].dropbox[dropOptIndex] = value;
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  // Handlers para campos condicionales dentro de secciones repetibles
  const handleAddSectionConditionalField = (inputId, fieldIndex, optionIndex) => {
    const updatedInputs = { ...form.inputs };
    const sectionField = updatedInputs[inputId].sectionFields?.[fieldIndex];
    if (sectionField) {
      if (!sectionField.conditionalFields) {
        sectionField.conditionalFields = {};
      }
      if (!sectionField.conditionalFields[optionIndex]) {
        sectionField.conditionalFields[optionIndex] = [];
      }
      sectionField.conditionalFields[optionIndex].push({
        title: '',
        name: '',
        type: 'TEXT',
        required: true,
        triggerOption: sectionField.dropbox?.[optionIndex] || ''
      });
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleRemoveSectionConditionalField = (inputId, fieldIndex, optionIndex, condFieldIndex) => {
    const updatedInputs = { ...form.inputs };
    const sectionField = updatedInputs[inputId].sectionFields?.[fieldIndex];
    if (sectionField?.conditionalFields?.[optionIndex]) {
      sectionField.conditionalFields[optionIndex].splice(condFieldIndex, 1);
      if (sectionField.conditionalFields[optionIndex].length === 0) {
        delete sectionField.conditionalFields[optionIndex];
      }
      if (Object.keys(sectionField.conditionalFields).length === 0) {
        delete sectionField.conditionalFields;
      }
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleSectionConditionalFieldChange = (inputId, fieldIndex, optionIndex, condFieldIndex, field, value) => {
    const updatedInputs = { ...form.inputs };
    const condField = updatedInputs[inputId].sectionFields?.[fieldIndex]?.conditionalFields?.[optionIndex]?.[condFieldIndex];
    if (condField) {
      condField[field] = value;
      if (field === 'title') {
        condField.name = value;
      }
      condField.triggerOption = updatedInputs[inputId].sectionFields[fieldIndex].dropbox?.[optionIndex] || '';
      if (field === 'type' && value === 'DROPBOX' && !condField.dropbox) {
        condField.dropbox = [''];
      }
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleAddSectionConditionalDropboxOption = (inputId, fieldIndex, optionIndex, condFieldIndex) => {
    const updatedInputs = { ...form.inputs };
    const condField = updatedInputs[inputId].sectionFields?.[fieldIndex]?.conditionalFields?.[optionIndex]?.[condFieldIndex];
    if (condField) {
      if (!condField.dropbox) {
        condField.dropbox = [];
      }
      condField.dropbox.push('');
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleRemoveSectionConditionalDropboxOption = (inputId, fieldIndex, optionIndex, condFieldIndex, dropOptIndex) => {
    const updatedInputs = { ...form.inputs };
    const condField = updatedInputs[inputId].sectionFields?.[fieldIndex]?.conditionalFields?.[optionIndex]?.[condFieldIndex];
    if (condField?.dropbox) {
      condField.dropbox.splice(dropOptIndex, 1);
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleSectionConditionalDropboxOptionChange = (inputId, fieldIndex, optionIndex, condFieldIndex, dropOptIndex, value) => {
    const updatedInputs = { ...form.inputs };
    const condField = updatedInputs[inputId].sectionFields?.[fieldIndex]?.conditionalFields?.[optionIndex]?.[condFieldIndex];
    if (condField?.dropbox) {
      condField.dropbox[dropOptIndex] = value;
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  // Handlers para sección repetible dentro de campos condicionales (dropbox)
  const handleConditionalRepeatableSectionFieldAdd = (inputId, optionIndex, condFieldIndex) => {
    const updatedInputs = { ...form.inputs };
    const condField = updatedInputs[inputId].conditionalFields?.[optionIndex]?.[condFieldIndex];
    if (condField) {
      if (!condField.sectionFields) condField.sectionFields = [];
      condField.sectionFields.push({ title: '', name: '', type: 'TEXT', required: true });
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleConditionalRepeatableSectionFieldRemove = (inputId, optionIndex, condFieldIndex, sectionFieldIndex) => {
    const updatedInputs = { ...form.inputs };
    const condField = updatedInputs[inputId].conditionalFields?.[optionIndex]?.[condFieldIndex];
    if (condField?.sectionFields) {
      condField.sectionFields.splice(sectionFieldIndex, 1);
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleConditionalRepeatableSectionFieldChange = (inputId, optionIndex, condFieldIndex, sectionFieldIndex, field, value) => {
    const updatedInputs = { ...form.inputs };
    const sectionField = updatedInputs[inputId].conditionalFields?.[optionIndex]?.[condFieldIndex]?.sectionFields?.[sectionFieldIndex];
    if (sectionField) {
      sectionField[field] = value;
      if (field === 'title') sectionField.name = value;
      if (field === 'type' && value === 'DROPBOX' && !sectionField.dropbox) sectionField.dropbox = [''];
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleConditionalRepeatableSectionDropboxOptionAdd = (inputId, optionIndex, condFieldIndex, sectionFieldIndex) => {
    const updatedInputs = { ...form.inputs };
    const sectionField = updatedInputs[inputId].conditionalFields?.[optionIndex]?.[condFieldIndex]?.sectionFields?.[sectionFieldIndex];
    if (sectionField) {
      if (!sectionField.dropbox) sectionField.dropbox = [];
      sectionField.dropbox.push('');
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleConditionalRepeatableSectionDropboxOptionRemove = (inputId, optionIndex, condFieldIndex, sectionFieldIndex, dropOptIndex) => {
    const updatedInputs = { ...form.inputs };
    const sectionField = updatedInputs[inputId].conditionalFields?.[optionIndex]?.[condFieldIndex]?.sectionFields?.[sectionFieldIndex];
    if (sectionField?.dropbox) {
      sectionField.dropbox.splice(dropOptIndex, 1);
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleConditionalRepeatableSectionDropboxOptionChange = (inputId, optionIndex, condFieldIndex, sectionFieldIndex, dropOptIndex, value) => {
    const updatedInputs = { ...form.inputs };
    const sectionField = updatedInputs[inputId].conditionalFields?.[optionIndex]?.[condFieldIndex]?.sectionFields?.[sectionFieldIndex];
    if (sectionField?.dropbox) {
      sectionField.dropbox[dropOptIndex] = value;
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  const handleConditionalRepeatableSectionUpdate = (inputId, optionIndex, condFieldIndex, updates) => {
    const updatedInputs = { ...form.inputs };
    const condField = updatedInputs[inputId].conditionalFields?.[optionIndex]?.[condFieldIndex];
    if (condField) {
      if (updates.minInstances != null) condField.minInstances = updates.minInstances;
      if (updates.maxInstances != null) condField.maxInstances = updates.maxInstances;
    }
    setForm({ ...form, inputs: updatedInputs });
  };

  return {
    // Input básicos
    handleTitleChange,
    handleInputUpdate,
    handleRequiredChange,
    handleSizeChange,
    handleRemoveInput,
    handleAddInput,
    handleDragEnd,
    
    // Opciones de dropbox
    handleAddOption,
    handleRemoveOption,
    handleOptionChange,
    
    // Campos condicionales
    handleAddConditionalField,
    handleRemoveConditionalField,
    handleConditionalFieldChange,
    handleAddConditionalDropboxOption,
    handleRemoveConditionalDropboxOption,
    handleConditionalDropboxOptionChange,

    // Campos condicionales anidados (dentro de dropbox condicional)
    handleAddNestedConditionalField,
    handleRemoveNestedConditionalField,
    handleNestedConditionalFieldChange,
    handleAddNestedConditionalDropboxOption,
    handleRemoveNestedConditionalDropboxOption,
    handleNestedConditionalDropboxOptionChange,
    
    // Secciones repetibles
    handleAddSectionField,
    handleRemoveSectionField,
    handleSectionFieldChange,
    handleAddSectionFieldDropboxOption,
    handleRemoveSectionFieldDropboxOption,
    handleSectionFieldDropboxOptionChange,
    
    // Campos condicionales dentro de secciones repetibles
    handleAddSectionConditionalField,
    handleRemoveSectionConditionalField,
    handleSectionConditionalFieldChange,
    handleAddSectionConditionalDropboxOption,
    handleRemoveSectionConditionalDropboxOption,
    handleSectionConditionalDropboxOptionChange,

    // Sección repetible dentro de campos condicionales (dropbox)
    handleConditionalRepeatableSectionFieldAdd,
    handleConditionalRepeatableSectionFieldRemove,
    handleConditionalRepeatableSectionFieldChange,
    handleConditionalRepeatableSectionDropboxOptionAdd,
    handleConditionalRepeatableSectionDropboxOptionRemove,
    handleConditionalRepeatableSectionDropboxOptionChange,
    handleConditionalRepeatableSectionUpdate,
  };
};

export default useFormHandlers;
